import React, { useEffect, useState, useContext } from 'react';
import AceEditor from 'react-ace';
import "ace-builds/src-noconflict/theme-chrome";
import "ace-builds/src-min-noconflict/mode-javascript"
import { Button, CircularProgress, Tooltip, Checkbox, FormControlLabel, withStyles } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { FirebaseContext } from '../firebase/context';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RestoreIcon from '@material-ui/icons/Restore';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useParams } from 'react-router-dom';

const defaultStepCode = [
    '// Get the text container\nconst container = document.querySelector("#step1-message");\n\n// Generate a random color code\nconst randomColor = `#${Math.floor(Math.random()*16777215).toString(16)}`;\n\n//set the container background color to the random color\ncontainer.style.backgroundColor = randomColor;'
]

let answerSaver;

const theme = 'chrome'

const GreenCheckbox = withStyles({
    root: {
      color: green[400],
      '&$checked': {
        color: green[600],
      },
    },
    checked: {},
  })((props) => <Checkbox color="default" {...props} />);

const ConfirmationDialog = ({open, handleClose, handleReset}) =>{
    return (
        <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">{"Reset your answer?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            You will restore the original state for this answer, and all progress for this question will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleReset} color="primary">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    )
}

const Editor = ({ activeStep, activeUser, isAdmin, selectedUser, defaultAnswer, inputType, onRun, onComplete }) => {
    const [codeString, setCodeString] = useState('')
    const [disabled, setDisabled] = useState(true);
    const firebase = useContext(FirebaseContext)
    const [saving, setSaving] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    let input;
    const params = useParams();
    const [isComplete, setIsComplete] = useState(false);

    //will be a timeout that can be set and cleared as the answer changes

    useEffect(() => {
        setCodeString('')
        //when the activeStep or selected user changes, get the candidates saved answer
        getAnswer()
    }, [activeStep, selectedUser])

    const getAnswer = () => {
        const db = firebase.firestore()
        //Answer documents in firestore will be the userId + active step
        const answerKey = `${selectedUser.uid}-${activeStep}`

        const docRef = db.collection('answers').doc(answerKey).get().then(res => {
            if (res.exists) {
                const data = res.data();
                setIsComplete(data?.complete || false);

                if (!!data.value) {
                    setCodeString(data.value)

                    //uncomment below to print the stringified answer
                    // console.log(JSON.stringify(data.value))
                }else{
                    setCodeString(defaultAnswer)
                }
            } else{
                setCodeString(defaultAnswer)
                setIsComplete(false);
            }
        }).catch(err => {
            setIsComplete(false);
            setCodeString(defaultAnswer || '')
        })
    }

    const disableEdit = ()=>{
        return isAdmin && (selectedUser.uid !== activeUser.uid)
    }

    const runCode = (codeString) => {
        setTimeout(()=>{
            (function () {
                try {
                    // console.log(JSON.stringify(codeString))
                    eval(codeString);
                } catch (err) {
                    console.log(err)
                }
            })();
        },0)
    }

    const resetCode = ()=>{
        setConfirmationOpen(true);
        setCodeString(defaultAnswer);
        setSaving(true);
        saveAnswer(defaultAnswer);
    }

    const saveAnswer = (value, complete) => {
        const role = params.role || 'se';
        const db = firebase.firestore();
        const answerKey = `${selectedUser.uid}-${activeStep}`;
        let saveBody;
        if(value){
            saveBody = {
                value: value,
                uid: selectedUser.uid,
                role: role,
                complete: complete || false
            }
        } else{
            saveBody = {
                uid: selectedUser.uid,
                role: role,
                complete: complete || false
            }
        }

        const docRef = db.collection('answers').doc(answerKey).set(saveBody, { merge: true }).then(res => {
            setSaving(false);
        })
    }

    const handleChange = (value) => {
        setSaving(true);
        setCodeString(value);
        

        //when it changes, stop the previous save and initiate a new save that waits X seconds
        clearTimeout(answerSaver)

        answerSaver = setTimeout(() => {
            saveAnswer(value)
        }, 1000)
    }

    const handleComplete = (e)=>{
        setIsComplete(e.target.checked)
        setSaving(true);
        saveAnswer(null, e.target.checked)
        onComplete(e.target.checked)
    }


    //Switch the editor between modes - code editor or text
    if(inputType === "code"){
        input = <AceEditor
        mode="javascript" // in the future this might be variable if the exercis takes other languages
        theme={theme}
        width="100%"
        height="calc(100vh - 120px)"
        showPrintMargin={false}
        onChange={(value, event) => { handleChange(value) }}
        defaultValue={codeString}
        readOnly={disableEdit()} //TODO: if this is the tutorial step, or if the active user doesn't match the selected user, disable
        value={codeString}
        setOptions={{ useWorker: false }}
    />
    } else {
        input = <textarea 
        style={{width:"100%", height:"400px"}}
        onChange={(event) => { handleChange(event.target.value) }}
        value={codeString}
        readOnly={disableEdit()}
        />
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>

            {/* TODO: possibly make this header part into it's own component */}
            <div style={{ display: 'flex', marginTop: 10, marginBottom: 10 }}>
                <div style={{ flexGrow: 1, display: 'flex' }}>

                <div>
                    {inputType === 'code' ?
                    <Tooltip
                        title="Test your code by running it!"
                        placement="right"
                    >
                        <Button color="secondary" variant="contained" style={{ width: 150 }} onClick={() => { onRun(); runCode(codeString); }}>Run<PlayCircleFilledIcon style={{ marginLeft: 10 }} /></Button>

                    </Tooltip> : ''
                    }
                    {inputType === 'code' ?
                    <Tooltip
                        title="Reset your code to the default state."
                        placement="right"
                    >
                        <Button color="secondary" variant="contained" style={{ width: 150, marginLeft: 10 }} onClick={() => {setConfirmationOpen(true); }}>Reset<RestoreIcon /></Button>

                    </Tooltip> : ''
                    }
                    <FormControlLabel
                        value="complete"
                        control={<GreenCheckbox checked={isComplete} onChange={e=>handleComplete(e)}/>}
                        label="Mark complete"
                        labelPlacement="start"
                        />
                </div>

                </div>
                <div style={{ width: 150, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <span style={{ marginRight: 10 }}>{saving ? 'Saving' : 'Saved'}</span>
                    {saving ? <CircularProgress size={25} /> : <CheckCircleIcon />}
                </div>
            </div>

            {input}
            <ConfirmationDialog open={confirmationOpen} handleClose={()=>setConfirmationOpen(false)} handleReset={()=>{resetCode(); setConfirmationOpen(!confirmationOpen)}}/>
        </div>
    );
}

export default Editor;